import { Link } from "react-router-dom";
import { useSeo } from "@/hooks/useSeo";
import { faqList } from "@/mocks/faq";
import { articles } from "@/mocks/articles";
import FaqAccordion from "@/components/feature/FaqAccordion";
import HomeHero from "./components/HomeHero";
import CategoryStrip from "./components/CategoryStrip";
import LatestFeed from "./components/LatestFeed";

const steps = [
  {
    icon: "ri-flashlight-line",
    title: "先看今日速览",
    text: "首屏会把当天讨论度上升最快的事件顶上来，用一分钟就能知道今天大家主要在聊什么。",
  },
  {
    icon: "ri-node-tree",
    title: "再看事件时间线",
    text: "每篇记录都按时间顺序排好起因、进展与回应，避免你在零散的片段里反复拼凑。",
  },
  {
    icon: "ri-shield-check-line",
    title: "分清事实与传闻",
    text: "已确认的公开信息和未经证实的说法会分开标注，官方回应会同步更新到记录末尾。",
  },
];

export default function Home() {
  useSeo({
    title: "52吃瓜网 | 今日热门 全网热议 最新大瓜事件汇总",
    description:
      "52吃瓜网实时汇总今日热门事件、最新大瓜与全网热议话题，覆盖娱乐八卦与社会奇闻，按热度梳理事件来龙去脉，让你一站跟上舆论风向。",
    keywords: "52吃瓜网,今日热门,最新大瓜,全网热议,事件汇总,娱乐八卦,热点话题榜",
    path: "/",
    jsonLd: [
      {
        "@context": "https://schema.org",
        "@type": "WebSite",
        name: "52吃瓜网",
        url: "https://kimchy.cn/",
        description: "今日热门与全网热议事件汇总门户",
        inLanguage: "zh-CN",
        potentialAction: {
          "@type": "SearchAction",
          target: "https://kimchy.cn/hot?q={search_term_string}",
          "query-input": "required name=search_term_string",
        },
      },
      {
        "@context": "https://schema.org",
        "@type": "Organization",
        name: "52吃瓜网",
        url: "https://kimchy.cn/",
        description: "把分散在各平台的热点线索整理成完整事件记录的中文资讯门户。",
      },
      {
        "@context": "https://schema.org",
        "@type": "ItemList",
        name: "今日热门事件汇总",
        itemListElement: articles.slice(0, 6).map((item, index) => ({
          "@type": "ListItem",
          position: index + 1,
          url: `https://kimchy.cn/article/${item.id}`,
          name: item.title,
        })),
      },
      {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: faqList.map((item) => ({
          "@type": "Question",
          name: item.q,
          acceptedAnswer: { "@type": "Answer", text: item.a },
        })),
      },
    ],
  });

  return (
    <>
      <HomeHero />
      <CategoryStrip />

      <section className="border-y border-background-200 bg-background-50 px-4 py-10 md:px-8 md:py-14">
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4">
          {[
            { label: "今日新增事件", value: "12", unit: "条", icon: "ri-add-circle-line" },
            { label: "在榜热议话题", value: "10", unit: "个", icon: "ri-fire-line" },
            { label: "覆盖主题分区", value: "5", unit: "类", icon: "ri-layout-grid-line" },
            { label: "平均整理时长", value: "18", unit: "分钟", icon: "ri-timer-line" },
          ].map((stat) => (
            <div
              key={stat.label}
              className="flex items-center gap-4 rounded-lg border border-background-200 bg-background-100 px-5 py-5"
            >
              <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-md bg-accent-100 text-xl text-accent-700">
                <i className={stat.icon}></i>
              </span>
              <div>
                <p className="font-heading text-2xl font-black text-foreground-950">
                  {stat.value}
                  <span className="ml-1 text-sm font-label font-normal text-foreground-500">
                    {stat.unit}
                  </span>
                </p>
                <p className="mt-0.5 text-xs font-label text-foreground-500">{stat.label}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      <LatestFeed />

      <section className="px-4 py-10 md:px-8 md:py-14">
        <div className="flex flex-col gap-2">
          <h2 className="font-heading text-xl font-black text-foreground-950 md:text-2xl">
            怎么在这里吃瓜
          </h2>
          <p className="text-sm font-label text-foreground-500">
            三步看清一件事，也顺手分辨哪些只是传闻。
          </p>
        </div>
        <div className="mt-6 grid grid-cols-1 gap-5 md:grid-cols-3">
          {steps.map((step, index) => (
            <div
              key={step.title}
              className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-6"
            >
              <div className="flex items-center justify-between">
                <span className="flex h-11 w-11 items-center justify-center rounded-md bg-secondary-100 text-xl text-secondary-700">
                  <i className={step.icon}></i>
                </span>
                <span className="font-heading text-3xl font-black text-background-300">
                  0{index + 1}
                </span>
              </div>
              <h3 className="mt-4 font-heading text-base font-bold text-foreground-950">
                {step.title}
              </h3>
              <p className="mt-2 text-sm leading-relaxed text-foreground-600 font-label">
                {step.text}
              </p>
            </div>
          ))}
        </div>
      </section>

      <section className="border-t border-background-200 bg-background-100 px-4 py-10 md:px-8 md:py-14">
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-3">
          <div className="lg:col-span-1">
            <h2 className="font-heading text-xl font-black text-foreground-950 md:text-2xl">
              常见问题
            </h2>
            <p className="mt-3 text-sm leading-relaxed text-foreground-600 font-label">
              关于内容来源、热度算法与线索投稿的高频疑问都整理在这里。如果还有没解答的地方，欢迎在联系我们页面留言。
            </p>
            <Link
              to="/contact"
              className="mt-5 inline-flex items-center gap-1.5 whitespace-nowrap rounded-full bg-primary-500 px-5 py-2.5 text-sm font-label text-background-50 transition-colors hover:bg-primary-600"
            >
              前往联系我们
              <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
          <div className="lg:col-span-2">
            <FaqAccordion />
          </div>
        </div>
      </section>
    </>
  );
}